/**
 * Ordre des horaires alternatifs d'un segment (TR-JOURNEY / TR-JOURNEY-NIGHT).
 *
 * Une offre porte souvent plusieurs journeys pour la même paire origine-destination :
 * le même prix, la même politique bagage, mais des horaires différents. Il faut donc
 * en désigner un — celui qui s'affiche sur la carte et qui part coché dans le panneau
 * « Choisissez vos horaires ».
 *
 * L'ordre :
 *
 * 1. **durée croissante** — le trajet le plus court gagne, c'est le critère principal ;
 * 2. **à durée égale, le vol de NUIT gagne** (`isNextDay`, càd `departureDate !==
 *    arrivalDate`). À temps de trajet identique le client préfère voler de nuit : il
 *    dort à bord, il ne perd pas une journée sur place, et sur le long-courrier c'est
 *    l'horaire que les agences proposent spontanément ;
 * 3. **à durée et à `isNextDay` égaux, le départ le plus tôt gagne** — comportement
 *    historique, conservé pour que l'ordre reste déterministe.
 *
 * UN SEUL comparateur pour les deux côtés : le serveur y prend son minimum
 * (`extractAvailableJourneys` → `defaultSelectedJourneyKeys`) et le panneau client y
 * prend son ordre d'affichage. Les avoir séparés les a déjà fait diverger — la première
 * carte du panneau n'était pas celle qui était cochée.
 *
 * Documentation : `business-rules/01-recherche-vol.md`, règles TR-JOURNEY et
 * TR-JOURNEY-NIGHT.
 */
/**
 * Comparateur `Array#sort` des horaires alternatifs d'un même segment.
 *
 * Le premier élément de la liste triée est l'horaire à pré-sélectionner.
 */
export function compareJourneyOptions(a, b) {
    if (a.duration !== b.duration)
        return a.duration - b.duration;
    // TR-JOURNEY-NIGHT : à durée égale, l'arrivée J+1 passe devant.
    if (a.isNextDay !== b.isNextDay)
        return a.isNextDay ? -1 : 1;
    if (a.departureDate !== b.departureDate) {
        return a.departureDate.localeCompare(b.departureDate);
    }
    return a.departureTime.localeCompare(b.departureTime);
}
/**
 * Horaire à pré-sélectionner parmi les alternatives d'un segment, ou `undefined` si la
 * liste est vide.
 *
 * Passe par un minimum plutôt qu'un tri : l'appelant n'a pas à copier le tableau pour
 * ne pas réordonner `AvailableJourneysForSegment.options`, dont l'ordre (heure de
 * départ) est un contrat de l'API.
 */
export function pickDefaultJourneyOption(options) {
    return options.reduce((best, option) => (!best || compareJourneyOptions(option, best) < 0 ? option : best), undefined);
}
